import api from './client';
import type {
  ApproveLeaveParams,
  GetLeaveRequestParams,
  GetCompanyLeaveBalanceByIdParams,
  GetEmployeeLeaveRequestByIdParams,
  GrantWalletBalanceParams,
  ActivateWalletParams,
  DeactivateWalletParams,
} from '@gokrew/models/leaves';
import type {
  GetWalletLedgerParams,
  ReverseLeaveTransactionParams,
} from '@gokrew/models/leave-transactions';

export async function getLeaveRequests(params: GetLeaveRequestParams) {
  const { companyId } = params;
  const res = await api.get(`/companies/${companyId}/leave-requests`, {
    params,
  });
  return res.data.data.data;
}

export async function approveEmployeeLeave(params: ApproveLeaveParams) {
  const { companyId, leaveRequestId } = params;
  const res = await api.put(
    `/companies/${companyId}/leave-requests-management/${leaveRequestId}/approve`
  );
  return res.data;
}

export async function getLeaveRequestsById(
  params: GetEmployeeLeaveRequestByIdParams
) {
  const { companyId, employeeId, page, limit, hidePast } = params;
  const res = await api.get(
    `/companies/${companyId}/employees/${employeeId}/leave-requests`,
    { params: { page, limit, hidePast } }
  );
  return res.data;
}

export async function getEmployeeLeaveWallets(
  params: GetCompanyLeaveBalanceByIdParams
) {
  const { companyId, employeeId, branchId } = params;

  const res = await api.get(
    `/companies/${companyId}/employees/${employeeId}/leave-wallets`,
    branchId ? { params: { branchId } } : undefined
  );

  return res.data.data;
}

export async function grantEmployeeLeaveWalletBalance({
  companyId,
  employeeId,
  walletId,
  amount,
  comment,
}: GrantWalletBalanceParams) {
  const res = await api.post(
    `/companies/${companyId}/employees/${employeeId}/leave-wallets/${walletId}/grant`,
    { amount, comment }
  );
  return res.data;
}

export async function activateEmployeeLeaveWallet({
  companyId,
  employeeId,
  walletId,
}: ActivateWalletParams) {
  const res = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/leave-wallets/${walletId}/activate`
  );
  return res.data;
}

export async function deactivateEmployeeLeaveWallet({
  companyId,
  employeeId,
  walletId,
  resetWallet,
}: DeactivateWalletParams) {
  const res = await api.patch(
    `/companies/${companyId}/employees/${employeeId}/leave-wallets/${walletId}/deactivate`,
    { resetWallet }
  );
  return res.data;
}

/** One wallet's ledger, newest first, with reversal state. */
export async function getWalletLedger({
  companyId,
  walletId,
  page,
  limit,
}: GetWalletLedgerParams) {
  const res = await api.get(
    `/companies/${companyId}/leave-transactions-history/wallets/${walletId}`,
    { params: { page, limit } }
  );
  return res.data.data;
}

/** Reverses a manual grant with an offsetting, linked entry. */
export async function reverseLeaveTransaction({
  companyId,
  transactionId,
  reason,
}: ReverseLeaveTransactionParams) {
  const res = await api.post(
    `/companies/${companyId}/leave-transactions-history/${transactionId}/reverse`,
    reason ? { reason } : {}
  );
  return res.data;
}
